// client/src/components/common/PhoneMapPanel.tsx
//
// A map on a phone page (ST-00207, m4b-contracts §5): a fixed-height panel (260 px) in the page
// flow, so the page scrolls past it instead of the map taking the whole screen and the thumb, with
// a 44 px "Full screen" toggle that lifts the panel over the viewport — a CSS overlay, because
// iPhone Safari has no element fullscreen. Full screen is a labelled modal dialog: the page behind
// is scroll-locked, Tab stays inside, Esc or "Exit full screen" returns. The map's own controls
// (zoom, Street / Satellite, Corridor) take 44 px targets inside the panel (PhoneLayout.css).
//
// Render the map with `className={PHONE_MAP_CANVAS}` on its container (CorridorMap `className`):
// the panel sizes that element, and in full screen stretches the map component's wrapper.

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useEscapeKey } from './pickerHooks';
import { focusableIn } from './PickerSheet';
import './PhoneLayout.css';

/** The map container's classes on a phone page: height from the panel, rounded, bordered. */
export const PHONE_MAP_CANVAS = 'phone-map__canvas relative z-0 overflow-hidden rounded-lg border border-slate-200';

export interface PhoneMapPanelProps {
  /** The panel's heading ("Map"). */
  title: string;
  /** What the map is of, for the full-screen dialog ("JX12KLGP" → "Map of JX12KLGP — full screen"). */
  subject?: string;
  children: ReactNode;
  /** Lines under the map (last position, "no track yet"). */
  footer?: ReactNode;
  /** Extra classes on the panel in the page (a card); full screen drops them. */
  className?: string;
}

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3.5 8V3.5H8M12 3.5h4.5V8M16.5 12v4.5H12M8 16.5H3.5V12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CollapseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M8 3.5V8H3.5M16.5 8H12V3.5M12 16.5V12h4.5M3.5 12H8v4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function PhoneMapPanel({ title, subject, children, footer, className = '' }: PhoneMapPanelProps) {
  const [full, setFull] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  useEscapeKey(full, () => setFull(false));

  // Full screen: lock the document and the scrolling ancestors (the app scrolls inside
  // .page-pane), keep the toggle focused (it becomes "Exit full screen").
  useEffect(() => {
    if (!full) return undefined;
    const locked: Array<[HTMLElement, string]> = [];
    const lock = (el: HTMLElement) => {
      locked.push([el, el.style.overflow]);
      el.style.overflow = 'hidden';
    };
    lock(document.documentElement);
    lock(document.body);
    for (let el = rootRef.current?.parentElement ?? null; el && el !== document.body; el = el.parentElement) {
      if (/(auto|scroll)/.test(window.getComputedStyle(el).overflowY)) lock(el);
    }
    toggleRef.current?.focus({ preventScroll: true });
    return () => {
      locked.reverse().forEach(([el, value]) => {
        el.style.overflow = value;
      });
    };
  }, [full]);

  const trapTab = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!full || e.key !== 'Tab') return;
    const items = focusableIn(rootRef.current);
    if (!items.length) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const dialogLabel = `${subject ? `${title} of ${subject}` : title} — full screen`;

  return (
    <section
      ref={rootRef}
      className={full ? 'phone-map phone-map--full' : `phone-map${className ? ` ${className}` : ''}`}
      aria-label={full ? dialogLabel : title}
      role={full ? 'dialog' : undefined}
      aria-modal={full ? true : undefined}
      tabIndex={full ? -1 : undefined}
      onKeyDown={trapTab}
      data-phone-map={full ? 'full' : 'panel'}
    >
      <div className="phone-map__head">
        <h2 className="phone-map__title">{full && subject ? `${title} · ${subject}` : title}</h2>
        <button
          ref={toggleRef}
          type="button"
          className="phone-map__toggle"
          onClick={() => setFull((f) => !f)}
          aria-label={full ? 'Close full screen' : `Show the ${title.toLowerCase()} full screen`}
        >
          {full ? <CollapseIcon /> : <ExpandIcon />}
          <span>{full ? 'Close' : 'Full screen'}</span>
        </button>
      </div>
      <div className="phone-map__body">{children}</div>
      {footer && <div className="phone-map__foot">{footer}</div>}
    </section>
  );
}
